<script setup>
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { XIcon } from '@heroicons/vue/outline'
import { onMounted, onUnmounted } from 'vue'

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  confirmText: {
    type: String,
    default: '',
  },
  cancelText: {
    type: String,
    default: '',
  },
  showClose: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['onClose', 'onCancel', 'onConfirm'])

const handleClose = () => {
  if (props.showClose) {
    emit('onClose')
  }
}

const handleEscape = (e) => {
  if (e.key === 'Escape' && props.isOpen && props.showClose) {
    emit('onClose')
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
})
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="handleClose" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/50" aria-hidden="true" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-md lg:min-w-[48rem] transform overflow-hidden rounded-lg bg-white shadow-xl transition-all"
            >
              <div class="relative border-b border-gray-200">
                <DialogTitle as="h3" class="py-4 text-lg font-bold text-center text-gray-900">
                  {{ title }}
                </DialogTitle>
                <button
                  v-if="showClose"
                  @click="handleClose"
                  class="absolute right-4 top-4 rounded-md p-1 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-700"
                >
                  <XIcon class="h-5 w-5 text-gray-900" />
                </button>
              </div>

              <div class="p-4">
                <slot></slot>
              </div>

              <div class="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button
                  v-if="cancelText"
                  @click="$emit('onCancel')"
                  class="rounded px-4 py-2 text-sm font-semibold text-blue-900 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {{ cancelText }}
                </button>
                <button
                  v-if="confirmText"
                  @click="$emit('onConfirm')"
                  class="rounded px-4 py-2 text-sm font-semibold text-white bg-blue-800 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {{ confirmText }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
